// Generic browser → S3 direct upload over a presigned PUT URL. Domain code
// supplies the presign step (a server function that authorizes the actor,
// picks the key namespace, and calls S3AttachmentRepository.presign_upload on
// the Python side); this module owns the PUT itself and its failure
// diagnostics. See the donor repo's upload-bid-event-attachment.ts for a
// domain-specific caller of this exact shape.

export interface PresignedUpload {
  url: string;
  key: string;
  /**
   * The content type that was actually signed into `url`, when the presign
   * normalises what it was given.
   *
   * S3 signs `Content-Type` into the URL, so the PUT must send back a
   * byte-identical string — but a presign is entitled to normalise (the catalog's
   * lowercases and trims, because `File.type` guarantees neither). When it does,
   * the value this module computed is no longer the value that was signed, and
   * sending it is a guaranteed 403 SignatureDoesNotMatch. Echoing the signed
   * string back closes that gap by construction rather than by both sides
   * agreeing to normalise identically. Optional: a presign that normalises
   * nothing can omit it, and the computed type stands.
   */
  contentType?: string;
}

/**
 * The presign step, generic in what it returns.
 *
 * Generic so a domain whose presign carries more than `url` + `key` — the
 * catalog's, which mints an image row and answers with its `imageId` — gets
 * those fields back from `uploadToStorage` instead of having to presign twice or
 * thread the result around the upload. `PresignedUpload` is the floor: this
 * module reads exactly `url` and `key`, and everything else it passes through
 * without looking.
 */
export type PresignUploadFn<P extends PresignedUpload = PresignedUpload> = (input: {
  filename: string;
  contentType: string;
  /**
   * The file's exact byte count.
   *
   * Passed because a presign may need to cap it, and may sign it into the URL so
   * the store enforces the cap rather than trusting this number (the catalog's
   * does both). A presign that doesn't care simply ignores it.
   */
  sizeBytes: number;
}) => Promise<P>;

export interface UploadedObject {
  key: string;
  filename: string;
  sizeBytes: number;
}

/**
 * What a completed upload is: the object, plus whatever else the presign said.
 *
 * `url` is deliberately dropped. It is a short-lived signed credential (300s),
 * and once the PUT has happened there is nothing left to do with it — carrying
 * it onward only invites it into a log line or a cache.
 */
export type UploadResult<P extends PresignedUpload = PresignedUpload> = UploadedObject &
  Omit<P, "url">;

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "s3";
  }
}

/** What the PUT answered, in the shape the diagnostics below already read. */
interface PutOutcome {
  ok: boolean;
  status: number;
  body: string;
}

/**
 * The PUT itself, over `XMLHttpRequest` rather than `fetch`.
 *
 * **`fetch` cannot report upload progress.** There is no event for bytes sent —
 * `ReadableStream` request bodies are the only route, and they are unimplemented
 * in Safari and require HTTP/2 elsewhere. XHR has reported it since forever via
 * `xhr.upload.onprogress`, which is what the donor uses (`postImage`'s
 * `uploadProgressCb`, surfaced as a determinate `LinearProgress`). Ported on the
 * owner's ruling D3 for #300, replacing a bar that could only pulse.
 *
 * The two failure shapes `fetch` distinguished are preserved exactly: a request
 * that never gets an HTTP response rejects (`onerror`), and a response that
 * arrives with a non-2xx status resolves with the body for the caller to read.
 */
function putBytes(
  url: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressFn,
): Promise<PutOutcome> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress !== undefined) {
      xhr.upload.onprogress = (event) => {
        // `lengthComputable` is false until the browser knows the total; a
        // fraction computed from a zero total is `NaN`, which would render as a
        // bar of no width and read as "stuck".
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      };
    }
    xhr.onload = () =>
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        body: typeof xhr.responseText === "string" ? xhr.responseText : "",
      });
    // Same opacity as `fetch`'s TypeError: the browser withholds the reason.
    xhr.onerror = () => reject(new Error("the PUT got no HTTP response"));
    xhr.onabort = () => reject(new Error("the PUT was aborted"));
    xhr.send(file);
  });
}

/** Called with the fraction of bytes sent so far, between 0 and 1. */
export type UploadProgressFn = (fraction: number) => void;

export async function uploadToStorage<P extends PresignedUpload>(
  file: File,
  presign: PresignUploadFn<P>,
  onProgress?: UploadProgressFn,
): Promise<UploadResult<P>> {
  // S3 signs the Content-Type into the presigned URL via PutObjectCommand.
  // The PUT request must carry the same Content-Type header or S3 returns 403
  // (signature mismatch).
  const contentType = file.type || "application/octet-stream";
  const presigned = await presign({
    filename: file.name,
    contentType,
    sizeBytes: file.size,
  });
  const { url, ...rest } = presigned;

  // What the presign says it signed wins over what we sent it. See
  // `PresignedUpload.contentType`: a normalising presign signs a string we do not
  // have, and guessing it again here is the 403 this line exists to prevent.
  const signedContentType = presigned.contentType ?? contentType;

  const origin = typeof window !== "undefined" ? window.location.origin : "(non-browser)";

  let putResponse: PutOutcome;
  try {
    putResponse = await putBytes(url, file, signedContentType, onProgress);
  } catch (err) {
    // A cross-origin fetch that never gets an HTTP response rejects with an
    // opaque TypeError ("Failed to fetch" / "NetworkError…" / "Load failed").
    // The browser withholds the reason from JS — the actual cause (a missing
    // bucket CORS rule, or an unreachable host) is only visible in the DevTools
    // Console/Network tab. Log the context we *do* have so the failure is
    // diagnosable without DevTools.
    console.error(
      "[storage-upload] PUT to storage failed before any HTTP response — the request " +
        "was blocked or the host was unreachable. Most often a missing S3 bucket " +
        "CORS rule for this origin, but indistinguishable in JS from a network outage.",
      {
        bucketHost: hostOf(url),
        origin,
        filename: file.name,
        contentType: signedContentType,
        sizeBytes: file.size,
        error: err,
      },
    );
    throw new Error(
      "Upload couldn't reach file storage (possible network or CORS issue). Please try again, or contact support if it persists.",
      { cause: err },
    );
  }

  if (!putResponse.ok) {
    // CORS succeeded but S3 rejected the request. The body is a readable XML
    // error document (e.g. <Code>SignatureDoesNotMatch</Code> / AccessDenied),
    // which distinguishes a signing/permission problem from a CORS block.
    const body = putResponse.body;
    const code = body.match(/<Code>([^<]+)<\/Code>/)?.[1];
    console.error("[storage-upload] storage rejected the PUT.", {
      bucketHost: hostOf(url),
      status: putResponse.status,
      s3Code: code ?? "(none)",
      filename: file.name,
      contentType: signedContentType,
      bodyPreview: body.slice(0, 300),
    });
    throw new Error(
      `Upload failed (${putResponse.status}${code ? `: ${code}` : ""}). Please try again, or contact support if it persists.`,
    );
  }

  return { ...rest, filename: file.name, sizeBytes: file.size } as UploadResult<P>;
}
